import { useRef, useState, ComponentProps, CSSProperties } from 'react';
import {
  blockHasValidContext,
  getAISupportedBlocks
} from 'shared-helpers/aiTools';
import { useReduxState } from 'hooks';
import {
  Button,
  EmptyIndicator,
  LoadingIndicator,
  ModalButtonWithForm
} from 'components/common';
import { useEditorContext } from 'components/editor';
import { AIGenerateContentMain, AIToolsStatus } from '.';

import type { AISupportedBlocks, AllBlocks } from 'shared-types';
import type { EditorBlockTypes } from 'types';
import type { ButtonCategory, ButtonSize } from 'components/common/Button';
import type { QuotaStatus } from './AIToolsStatus';

export type FormValues = {
  blockIds: Record<string, true>;
  context?: string;
  chapterId?: number;
  numberOfBlocks?: number;
  prompt?: string;
  selectedBlocks: number[];
  type: AISupportedBlocks;
  useContentFromSelf: boolean;
  useCourseAndChapterTitle: boolean;
  useExternalKnowledge?: boolean;
};

export type View = 'form' | 'results' | 'selection';

type Props = {
  buttonProps?: ComponentProps<typeof Button>;
  category?: ButtonCategory;
  size?: ButtonSize;
  style?: CSSProperties;
} & (
  | { blockData: AllBlocks; blockId: number; type: EditorBlockTypes }
  | { blockData?: never; blockId?: never; type?: never }
);

const AIGenerateContentButton = ({
  blockData,
  blockId,
  buttonProps,
  category,
  size,
  style,
  type
}: Props) => {
  const { editorAIEnabled, editorBlocks } = useEditorContext();

  const {
    messages,
    serverConfig: { aiConfig }
  } = useReduxState();

  const [analyticsAIActionId, setAnalyticsAIActionId] = useState<number>(null);
  const [enableByQuota, setEnableByQuota] = useState<QuotaStatus>('loading');
  const [view, setView] = useState<View>('form');
  const [renderForm, setRenderForm] = useState(false);

  // handlers refs
  const onFormSubmit =
    useRef<(values: FormValues) => Promise<boolean>>(undefined);
  const onSelectionSubmit = useRef<(values: FormValues) => boolean>(undefined);
  const onResultsSubmit = useRef<(values: FormValues) => boolean>(undefined);

  if (!editorAIEnabled) return null; // early return to avoid needless work

  const handleKeepOpen = () => {
    if (view === 'selection') {
      setView('form');
      return true;
    }
    return false;
  };

  const handleHide = () => {
    setAnalyticsAIActionId(null);
    setView('form');
    setEnableByQuota('loading');
    setRenderForm(false);
  };

  const handleSubmit = async (values: FormValues) => {
    const handler = {
      form: onFormSubmit.current,
      results: onResultsSubmit.current,
      selection: onSelectionSubmit.current
    }[view];
    if (!handler) throw new Error('No handler found for view');

    return handler(values);
  };

  const handleTryAgain = () => {
    setAnalyticsAIActionId(null);
    setView('form');
  };

  const availableBlockTypes = getAISupportedBlocks(aiConfig?.allowedBlockTypes);
  if (
    !availableBlockTypes.length ||
    (type && !availableBlockTypes.includes(type as any))
  ) {
    return null;
  }

  const baseBlockHasContext = blockId
    ? blockHasValidContext({ block: editorBlocks[blockId] })
    : false;

  const initialValues = {
    blockIds: {},
    numberOfBlocks: 1,
    selectedBlocks: [],
    useContentFromSelf: baseBlockHasContext,
    useCourseAndChapterTitle: true,
    useExternalKnowledge: true,
    ...(type ? { type: type as AISupportedBlocks } : { type: 'text' })
  } satisfies FormValues;

  return (
    <ModalButtonWithForm
      buttonCategory={category || 'light'}
      buttonIcon="wand-magic-sparkles"
      buttonProps={{
        tooltip: messages.aiGenerateContent,
        ...buttonProps
      }}
      buttonSize={size || 'small'}
      buttonStyle={style}
      contentStyle={{ padding: 0 }}
      footerCancelButtonText={
        view === 'selection'
          ? messages.commonCancelAndGoBack
          : messages.commonClose
      }
      footerContent={
        renderForm ? (
          <>
            <div
              style={{
                display: view === 'form' ? 'block' : 'none',
                float: 'left',
                textAlign: 'center'
              }}
            >
              <AIToolsStatus
                onReady={(status) => setEnableByQuota(status)}
                style={{ padding: 0 }}
              />
            </div>

            <div
              style={{
                display: view === 'results' ? 'block' : 'none',
                float: 'left',
                textAlign: 'center'
              }}
            >
              <Button
                category="light"
                onClick={handleTryAgain}
                text={messages.commonTryAgain}
              />
            </div>
          </>
        ) : null
      }
      initialValues={initialValues}
      modalActionCategory={view === 'results' ? 'success' : 'default'}
      modalActionText={
        view === 'selection'
          ? messages.commonSaveSelection
          : view === 'results' && blockId
          ? messages.aiGenerateContentReplace
          : view === 'results'
          ? messages.aiGenerateContentAddToCourse
          : messages.commonCreate
      }
      modalStyle={{ maxWidth: 750 }}
      modalTitle={messages.aiGenerateContent}
      onHide={handleHide}
      onKeepOpen={handleKeepOpen}
      onShow={() => setRenderForm(true)}
      onSubmit={handleSubmit}
      stopPropagation={true}
      submitDisabled={enableByQuota !== 'allowed'}
    >
      {() =>
        enableByQuota === 'allowed' ? (
          <AIGenerateContentMain
            analyticsAIActionId={analyticsAIActionId}
            availableBlockTypes={availableBlockTypes}
            blockData={blockData}
            blockId={blockId}
            blockIdHasContext={baseBlockHasContext}
            getFormSubmit={onFormSubmit}
            getResultsSubmit={onResultsSubmit}
            getSelectionSubmit={onSelectionSubmit}
            onViewChange={setView}
            setAnalyticsAIActionId={setAnalyticsAIActionId}
            type={type as AISupportedBlocks}
            view={view}
          />
        ) : enableByQuota === 'loading' ? (
          <LoadingIndicator />
        ) : (
          <EmptyIndicator
            description={messages.aiToolsQuotaExceeded}
            icon="wand-magic-sparkles"
            title={messages.aiToolsDisabled}
          />
        )
      }
    </ModalButtonWithForm>
  );
};

export default AIGenerateContentButton;
